<template>
  <main class="hp-root">

    <!-- ══ 1. Banner peek 轮播（无缝循环） ══ -->
    <section class="hp-banner-section">
      <div class="hp-banner-viewport">
        <div
          class="hp-banner-track"
          :class="{ 'is-no-transition': !bannerTransition }"
          :style="{ transform: bannerTrackTransform }"
          @transitionend="onBannerTransitionEnd"
        >
          <div
            v-for="(item, idx) in loopSlides"
            :key="item.id"
            class="hp-banner-slide"
            :class="{ 'is-active': idx === trackIndex }"
            @click="onBannerSlideClick(idx, item)"
          >
            <img :src="item.imageUrl" :alt="item.title" class="hp-banner-img" loading="lazy" />
          </div>
        </div>
        <!-- 左右渐变遮罩，聚焦中间 banner -->
        <div class="hp-banner-fade hp-banner-fade--left" aria-hidden="true"></div>
        <div class="hp-banner-fade hp-banner-fade--right" aria-hidden="true"></div>
        <button class="hp-banner-nav hp-banner-nav--prev" @click="prevBanner" aria-label="上一张">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <button class="hp-banner-nav hp-banner-nav--next" @click="nextBanner" aria-label="下一张">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
        <div class="hp-banner-dots">
          <span
            v-for="(_, i) in banners"
            :key="i"
            class="hp-banner-dot"
            :class="{ 'is-active': i === realBannerIndex }"
            @click="goToBanner(i)"
          ></span>
        </div>
      </div>
    </section>

    <!-- ══ 2. 功能入口（通栏单行） ══ -->
    <section class="hp-entry-section">
      <router-link
        v-for="entry in entryCards"
        :key="entry.id"
        :to="entry.to"
        class="hp-entry-card"
        :class="[
          `hp-entry-card--${entry.id}`,
          { 'hp-entry-card--featured': entry.featured },
        ]"
      >
        <div class="hp-entry-glow" aria-hidden="true"></div>
        <div
          class="hp-entry-icon"
          :class="{ 'hp-entry-icon--sm': !entry.featured }"
          aria-hidden="true"
        >
          <svg v-if="entry.icon === 'image'" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
          <svg v-else-if="entry.icon === 'video'" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
          <svg v-else-if="entry.icon === 'apps'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>
          <svg v-else-if="entry.icon === 'workflow'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="6" cy="6" r="2.5"/><circle cx="18" cy="6" r="2.5"/><circle cx="12" cy="18" r="2.5"/><path d="M8.2 7.5l5.6 3M15.8 7.5l-5.6 3"/></svg>
          <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z"/></svg>
        </div>
        <div class="hp-entry-content">
          <div class="hp-entry-title-row">
            <span class="hp-entry-name">{{ entry.name }}</span>
            <span v-if="entry.badge" class="hp-entry-badge">{{ entry.badge }}</span>
          </div>
          <span class="hp-entry-desc">{{ entry.desc }}</span>
        </div>
        <svg class="hp-entry-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
      </router-link>
    </section>

    <!-- ══ 3. AI应用（灵感广场发现页风格） ══ -->
    <section v-if="inspSectionsLoading || visibleInspirationSections.length" class="hp-aiapp-section">
      <div class="hp-aiapp-hd hp-section-hd--full">
        <div class="hp-aiapp-hd-main">
          <h2 class="hp-section-title">AI应用</h2>
          <p class="hp-aiapp-subtitle">零门槛，即开即用，探索海量 AI 创意应用</p>
        </div>
        <router-link to="/inspiration" class="hp-section-more">探索更多
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
        </router-link>
      </div>

      <div v-if="inspSectionsLoading" class="hp-state-loading">
        <span class="hp-spinner"></span><span>加载中…</span>
      </div>

      <div v-else class="ip-discover hp-aiapp-discover">
        <div
          v-for="section in visibleInspirationSections"
          :key="section.id"
          class="ip-section"
        >
          <div
            class="ip-section-info"
            :style="{ '--cat-color': getCategoryColor(section.tabId) }"
            role="button"
            tabindex="0"
            @click="goInspirationTab(section.tabId)"
            @keypress.enter="goInspirationTab(section.tabId)"
          >
            <div class="ip-section-head">
              <span class="ip-section-tag">{{ section.tag }}</span>
              <h3 class="ip-section-title">{{ section.title }}</h3>
              <p class="ip-section-desc">{{ section.desc }}</p>
            </div>

            <div class="ip-section-cover" aria-hidden="true">
              <div class="ip-section-fan">
                <img
                  v-for="(card, fi) in getSectionFanCards(section)"
                  :key="card.id"
                  :src="card.cover"
                  :alt="card.title"
                  class="ip-section-fan-card"
                  :class="`ip-section-fan-card--${fi}`"
                  loading="lazy"
                />
              </div>
            </div>

            <span class="ip-section-more">
              查看全部
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </span>
          </div>

          <div class="ip-track-wrap">
            <button
              class="ip-nav-btn ip-nav-btn--prev"
              @click="scrollInspCards(section.id, -1)"
              aria-label="向左滚动"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>

            <div class="ip-hscroll" :ref="`track-${section.id}`">
              <div
                v-for="card in section.cards"
                :key="card.id"
                class="ip-hcard"
                @mouseenter="onVideoEnter(card, 'hvid')"
                @mouseleave="onVideoLeave(card, 'hvid')"
                @click="openInspirationDetail(card, section.tabId)"
              >
                <div class="ip-hcard-media">
                  <img :src="card.cover" :alt="card.title" loading="lazy" class="ip-hcard-img" />
                  <video
                    v-if="card.type === 'video'"
                    :ref="`hvid-${card.id}`"
                    class="ip-hcard-video"
                    loop
                    playsinline
                    preload="none"
                  ></video>
                  <div v-if="card.type === 'video'" class="ip-hcard-play-icon" aria-hidden="true">
                    <svg width="28" height="28" viewBox="0 0 32 32">
                      <circle cx="16" cy="16" r="15" fill="rgba(0,0,0,0.45)" stroke="rgba(255,255,255,0.3)" stroke-width="1"/>
                      <polygon points="13 10 22 16 13 22 13 10" fill="white"/>
                    </svg>
                  </div>
                  <div class="ip-card-dock">
                    <span class="ip-card-title">{{ card.title }}</span>
                    <button
                      type="button"
                      class="ip-same-btn"
                      @click.stop="onSameStyle(card, section.tabId)"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                        <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                      </svg>
                      一键同款
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <button
              class="ip-nav-btn ip-nav-btn--next"
              @click="scrollInspCards(section.id, 1)"
              aria-label="向右滚动"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- ══ 4. 工作流 ══ -->
    <section class="hp-wf-section">
      <div class="hp-section-hd hp-section-hd--full">
        <div class="hp-aiapp-hd-main">
          <h2 class="hp-section-title">工作流</h2>
          <p class="hp-aiapp-subtitle">各类型扣子工作流，精选 SVIP 免费体验</p>
        </div>
        <router-link to="/workflow-store" class="hp-section-more">查看更多
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
        </router-link>
      </div>
      <div v-if="wfLoading && !svipWorkflows.length" class="hp-state-loading">
        <span class="hp-spinner"></span><span>加载中…</span>
      </div>
      <div v-else-if="!wfLoading && !svipWorkflows.length" class="hp-state-empty">暂无工作流</div>
      <div v-else class="hp-wf-grid">
        <div
          v-for="wf in svipWorkflows"
          :key="wf.id"
          class="ip-hcard hp-wf-card"
          :class="{ 'is-wf-thumb-hovering': wfThumbHoverActive(wf) }"
          @mouseenter="onWfThumbEnter(wf)"
          @mouseleave="onWfThumbLeave(wf)"
          @click="$router.push({ name: 'WorkflowDetail', params: { id: wf.id } })"
        >
          <div class="ip-hcard-media">
            <div class="hp-wf-media-stack">
              <img :src="wf.image" :alt="wf.title" class="ip-hcard-img hp-wf-cover-img" loading="lazy" />
              <template v-if="wfThumbHoverMedia(wf)">
                <img
                  v-if="!wfThumbHoverMedia(wf).isVideo"
                  :src="wfThumbHoverMedia(wf).url"
                  :alt="wf.title"
                  class="hp-wf-hover-layer hp-wf-hover-img"
                  loading="lazy"
                />
                <video
                  v-else
                  :ref="`wfvid-${wf.id}`"
                  class="hp-wf-hover-layer hp-wf-hover-video"
                  loop
                  playsinline
                  muted
                  preload="none"
                ></video>
              </template>
            </div>
            <span class="hp-wf-svip-badge">SVIP免费</span>
            <div class="ip-card-dock">
              <span class="ip-card-title">{{ wf.title }}</span>
              <button
                type="button"
                class="ip-same-btn"
                @click.stop="$router.push({ name: 'WorkflowDetail', params: { id: wf.id } })"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
                查看详情
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <InspirationDetailModal
      ref="inspDetailModal"
      :categories="categories"
      @same-style="onSameStyle"
    />
  </main>
</template>

<script>
import { getHomeBannerContentList } from '@/api/agents'
import { getInspirationCategoryList, getInspirationList } from '@/api/inspiration'
import { getWorkflowList } from '@/api/workflow'
import {
  mapInspirationItemToCard,
  sortInspirationCategories,
  mapCategoryToDiscoverSection,
  isInspirationApiSuccess,
} from '@/utils/inspirationMappers'
import { rowsToNormalizedWorkflows, extractWorkflowRows, isBizSuccess } from '@/utils/workflowNormalize'
import { firstWorkflowFileHoverMedia } from '@/utils/workflowMedia'
import { navigateInspirationSameStyle } from '@/utils/inspirationApply'
import { requireLogin } from '@/utils/authGuard'
import {
  stopAllInspirationHoverVideos,
  stopInspirationDetailModalVideos,
  stopInspirationHoverVideo,
} from '@/utils/inspirationCardVideo'
import { parseInspirationParamInfo } from '@/utils/inspirationMappers'
import InspirationDetailModal from '@/components/InspirationDetailModal.vue'
import { buildAgentHubPath } from '@/views/digital-employee/agentRegistry'

const HOME_QUICK_EMP_ID = '2'

const CATEGORY_COLOR_PALETTE = [
  '#e11d48',
  '#f59e0b',
  '#3b82f6',
  '#8b5cf6',
  '#06b6d4',
  '#10b981',
  '#f97316',
]

const CATEGORY_DESC_MAP = {
  'Seedance2.0': '全球首款同时支持首尾帧和音频的旗舰视频模型',
  '达人种草': '达人拓展化种草，自然演绎促转化',
  '创意特效': '高级创意特效生成，视觉冲击力强，适合品牌发布与短视频包装',
  '商品主图': '为商品量身打造，一键解锁高级质感商业大片',
  '营销海报': '节日氛围感爆棚，一键生成高质感宣传海报',
  '社媒口播': '社媒口播内容生成，适合短视频投放与品牌传播',
}

const DISCOVER_PAGE_SIZE = 14
const WF_MAX_ITEMS = 16

const FALLBACK_BANNERS = [
  {
    id: 'fb1',
    imageUrl: 'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=1400&q=80',
    title: '快捷创作重磅上线<br>多种模型一键切换',
    tag: '新功能上线',
    link: buildAgentHubPath(HOME_QUICK_EMP_ID, 'quick-ecom'),
  },
  {
    id: 'fb2',
    imageUrl: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1400&q=80',
    title: 'AI应用 · 零门槛即开即用<br>海量模板任你选',
    tag: 'AI应用',
    link: '/agents-center',
  },
  {
    id: 'fb3',
    imageUrl: 'https://images.unsplash.com/photo-1535016120720-40c646be5580?w=1400&q=80',
    title: '全能视频 · 创意视频一键生成<br>专业品质触手可及',
    tag: '全能视频',
    link: buildAgentHubPath(HOME_QUICK_EMP_ID, 'quick-video'),
  },
]

export default {
  name: 'HomePage',

  components: {
    InspirationDetailModal,
  },

  data() {
    return {
      banners: [],
      trackIndex: 2,
      bannerTransition: true,
      bannerTimer: null,

      entryCards: [
        {
          id: 'quick-img',
          name: '快捷出图',
          desc: '多模型一键切换，秒级生成电商主图与素材',
          to: buildAgentHubPath(HOME_QUICK_EMP_ID, 'quick-ecom'),
          featured: true,
          badge: '热门',
          icon: 'image',
        },
        {
          id: 'quick-video',
          name: '全能视频',
          desc: '创意视频一键生成，口播特效专业品质',
          to: buildAgentHubPath(HOME_QUICK_EMP_ID, 'quick-video'),
          featured: true,
          badge: '推荐',
          icon: 'video',
        },
        { id: 'ai-apps', name: '爆款书单', desc: '一键复刻爆款书单视频', to: buildAgentHubPath(HOME_QUICK_EMP_ID, 'hotvideo'), icon: 'apps' },
        { id: 'workflow', name: '工作流', desc: '各类型扣子工作流', to: '/workflow-store', icon: 'workflow' },
        { id: 'inspiration', name: '灵感广场', desc: '点滴灵感，大大创意', to: '/inspiration', icon: 'spark' },
      ],

      // AI应用（发现页风格）
      inspirationSections: [],
      categories: [],
      inspSectionsLoading: false,

      // 工作流
      svipWorkflows: [],
      wfLoading: false,
      hoveredWfThumbId: null,
    }
  },

  async created() {
    this.loadBanners()
    this.loadInspirationSections()
    this.loadSvipWorkflows()
  },

  mounted() {
    this.startBannerTimer()
  },

  beforeDestroy() {
    this.stopBannerTimer()
    this.stopAllInspirationPreviewVideos()
    this.svipWorkflows.forEach((wf) => this.onWfThumbLeave(wf))
  },

  beforeRouteLeave(to, from, next) {
    this.stopAllInspirationPreviewVideos()
    next()
  },

  computed: {
    loopSlides() {
      const list = this.banners
      if (list.length <= 1) return list
      const first = list[0]
      const last = list[list.length - 1]
      return [
        { ...last, id: `clone-last-${last.id}`, clone: true },
        ...list,
        { ...first, id: `clone-first-${first.id}`, clone: true },
      ]
    },
    realBannerIndex() {
      if (this.banners.length <= 1) return 0
      const len = this.loopSlides.length
      if (this.trackIndex <= 0) return this.banners.length - 1
      if (this.trackIndex >= len - 1) return 0
      return this.trackIndex - 1
    },
    bannerTrackTransform() {
      return `translateX(calc(var(--hp-banner-offset) - ${this.trackIndex} * (var(--hp-banner-slide) + var(--hp-banner-gap))))`
    },
    visibleInspirationSections() {
      return this.inspirationSections.filter(
        (sec) => Array.isArray(sec.cards) && sec.cards.length > 0
      )
    },
  },

  methods: {
    // ── Banner ──
    mapContentItemToBanner(item, index) {
      const imageUrl = String(item.samplePic || '').trim()
      if (!imageUrl) return null
      return {
        id: String(item.id ?? index),
        imageUrl,
        title: String(item.sampleTitle || '').trim(),
        link: String(item.sampleResult || '').trim(),
      }
    },
    navigateBannerLink(link) {
      const url = String(link || '').trim()
      if (!url) return
      if (/^https?:\/\//i.test(url)) {
        window.open(url, '_blank', 'noopener,noreferrer')
        return
      }
      const path = url.startsWith('/') ? url : `/${url}`
      this.$router.push(path).catch(() => {})
    },
    async loadBanners() {
      try {
        const res = await getHomeBannerContentList()
        const ok = res && (res.code === 0 || res.code === '0')
        const list = ok ? (res.dataList || res.data || []) : []
        if (Array.isArray(list) && list.length) {
          this.banners = list
            .map((item, i) => this.mapContentItemToBanner(item, i))
            .filter(Boolean)
        }
      } catch (err) {
        console.warn('[HomePage] loadBanners failed', err)
      }
      if (!this.banners.length) this.banners = FALLBACK_BANNERS
      this.initBannerTrack()
    },
    initBannerTrack() {
      if (this.banners.length >= 2) {
        // 默认居中第 2 张，左侧露出第 1 张，避免左边空白
        this.trackIndex = 2
      } else if (this.banners.length === 1) {
        this.trackIndex = 0
      } else {
        this.trackIndex = 0
      }
      this.bannerTransition = true
    },
    prevBanner() {
      if (this.banners.length <= 1) return
      this.bannerTransition = true
      this.trackIndex -= 1
      this.resetBannerTimer()
    },
    nextBanner() {
      if (this.banners.length <= 1) return
      this.bannerTransition = true
      this.trackIndex += 1
      this.resetBannerTimer()
    },
    goToBanner(realIndex) {
      if (this.banners.length <= 1) return
      this.bannerTransition = true
      this.trackIndex = realIndex + 1
      this.resetBannerTimer()
    },
    onBannerSlideClick(idx, item) {
      if (idx === this.trackIndex) {
        if (!item.clone && item.link) this.navigateBannerLink(item.link)
        return
      }
      this.bannerTransition = true
      this.trackIndex = idx
      this.resetBannerTimer()
    },
    onBannerTransitionEnd(e) {
      if (e.propertyName !== 'transform') return
      const len = this.loopSlides.length
      if (len <= 1) return
      if (this.trackIndex >= len - 1) {
        this.bannerTransition = false
        this.trackIndex = 1
      } else if (this.trackIndex <= 0) {
        this.bannerTransition = false
        this.trackIndex = len - 2
      }
    },
    startBannerTimer() { this.bannerTimer = setInterval(this.nextBanner, 5000) },
    stopBannerTimer() { if (this.bannerTimer) { clearInterval(this.bannerTimer); this.bannerTimer = null } },
    resetBannerTimer() { this.stopBannerTimer(); this.startBannerTimer() },

    // ── AI应用（发现页风格） ──
    getCategoryDesc(name) {
      const key = String(name || '').trim()
      return CATEGORY_DESC_MAP[key] || '通过 AI 技术生成的高质量创意内容，一键复刻同款风格。'
    },
    getCategoryColor(tabId) {
      const idx = this.categories.findIndex((c) => String(c.id) === String(tabId))
      if (idx >= 0) return CATEGORY_COLOR_PALETTE[idx % CATEGORY_COLOR_PALETTE.length]
      return '#6b7280'
    },
    getSectionFanCards(section) {
      return (section.cards || []).slice(0, 3)
    },
    scrollInspCards(sectionId, dir) {
      const el = this.$refs[`track-${sectionId}`]
      if (!el) return
      const track = Array.isArray(el) ? el[0] : el
      if (!track) return
      const card = track.querySelector('.ip-hcard')
      if (!card) return
      const gap = 10
      const step = (card.offsetWidth + gap) * 2
      track.scrollBy({ left: dir * step, behavior: 'smooth' })
    },
    goInspirationTab(tabId) {
      this.$router.push({ path: '/inspiration', query: { tab: tabId } }).catch(() => {})
    },
    async loadInspirationSections() {
      this.inspSectionsLoading = true
      try {
        const res = await getInspirationCategoryList()
        if (!isInspirationApiSuccess(res)) return
        const list = sortInspirationCategories(Array.isArray(res.data) ? res.data : [])
        this.categories = list
        this.inspirationSections = list.map((cat) => {
          const name = String(cat.name || '').trim()
          return mapCategoryToDiscoverSection(cat, this.getCategoryDesc(name))
        })
        await Promise.all(this.inspirationSections.map((sec) => this.loadSectionCards(sec)))
      } catch (err) {
        console.warn('[HomePage] loadInspirationSections failed', err)
      } finally {
        this.inspSectionsLoading = false
      }
    },
    async loadSectionCards(sec) {
      try {
        const res = await getInspirationList({
          pageNum: 1,
          pageSize: DISCOVER_PAGE_SIZE,
          categoryIds: sec.id,
        })
        if (!isInspirationApiSuccess(res)) {
          sec.cards = []
          return
        }
        const list = Array.isArray(res.dataList) ? res.dataList : []
        sec.cards = list.map((item) => mapInspirationItemToCard(item, sec.id)).filter(Boolean)
      } catch (err) {
        sec.cards = []
        console.warn('[HomePage] loadSectionCards failed', sec.id, err)
      }
    },

    getCardPreviewVideoUrl(card) {
      if (!card || card.type !== 'video') return ''
      return String(card.previewVideoUrl || card.coverUrls?.[1] || '').trim()
    },
    getVideoEl(card, prefix = 'hvid') {
      const refs = this.$refs[`${prefix}-${card.id}`]
      return Array.isArray(refs) ? refs[0] : refs
    },
    onVideoEnter(card, prefix = 'hvid') {
      if (card.type !== 'video') return
      const vid = this.getVideoEl(card, prefix)
      if (!vid) return
      const url = this.getCardPreviewVideoUrl(card)
      if (!url) return
      if (vid._hoverPlayHandler) {
        vid.removeEventListener('canplay', vid._hoverPlayHandler)
        vid._hoverPlayHandler = null
      }
      if (vid.dataset.loadedUrl !== url) {
        vid.src = url
        vid.dataset.loadedUrl = url
        vid.load()
      }
      vid.muted = false
      vid.volume = 0.8
      vid.loop = true
      const tryPlay = () => {
        if (!vid.src || vid.dataset.loadedUrl !== url) return
        vid.currentTime = 0
        vid.muted = false
        vid.volume = 0.8
        vid.play().catch(() => {
          vid.muted = true
          vid.play().catch(() => {})
        })
      }
      if (vid.readyState >= 3) { tryPlay() }
      else {
        vid._hoverPlayHandler = tryPlay
        vid.addEventListener('canplay', vid._hoverPlayHandler, { once: true })
      }
      vid.classList.add('ip-card-video--playing')
    },
    onVideoLeave(card, prefix = 'hvid') {
      if (card.type !== 'video') return
      const vid = this.getVideoEl(card, prefix)
      if (!vid) return
      stopInspirationHoverVideo(vid)
    },

    stopAllInspirationPreviewVideos() {
      stopAllInspirationHoverVideos(this)
      stopInspirationDetailModalVideos(this.$refs.inspDetailModal)
    },

    openInspirationDetail(card, tabId) {
      this.stopAllInspirationPreviewVideos()
      if (!card || card.id == null) return
      const modal = this.$refs.inspDetailModal
      if (modal && typeof modal.open === 'function') {
        modal.open(card, tabId || card.tabId || '')
      }
    },
    async onSameStyle(card, tabId) {
      if (!card || card.id == null) return
      if (!requireLogin(this, '请先登录后使用一键同款功能')) return
      const paramInfo = parseInspirationParamInfo(card?.paramInfo)
      const fetchDetail = !(paramInfo.router_url || paramInfo.routerUrl)
      try {
        await navigateInspirationSameStyle(this.$router, card, {
          tabId: tabId || card.tabId || '',
          fetchDetail,
        })
      } catch (err) {
        console.warn('[HomePage] onSameStyle failed', err)
        if (this.$message) this.$message.warning(err?.message || '一键同款失败')
      }
    },

    // ── 工作流 ──
    wfThumbHoverMedia(wf) {
      if (!wf) return null
      if (wf.thumbHoverMedia) return wf.thumbHoverMedia
      return firstWorkflowFileHoverMedia(wf.workflowFiles)
    },
    wfThumbHoverActive(wf) {
      return !!wf && this.hoveredWfThumbId === wf.id && !!this.wfThumbHoverMedia(wf)
    },
    getWfVideoEl(wf) {
      const refs = this.$refs[`wfvid-${wf.id}`]
      return Array.isArray(refs) ? refs[0] : refs
    },
    onWfThumbEnter(wf) {
      const media = this.wfThumbHoverMedia(wf)
      if (!media) return
      this.hoveredWfThumbId = wf.id
      if (!media.isVideo) return
      this.$nextTick(() => {
        const vid = this.getWfVideoEl(wf)
        if (!vid) return
        const url = String(media.url || '').trim()
        if (!url) return
        if (vid._hoverPlayHandler) {
          vid.removeEventListener('canplay', vid._hoverPlayHandler)
          vid._hoverPlayHandler = null
        }
        if (vid.dataset.loadedUrl !== url) {
          vid.src = url
          vid.dataset.loadedUrl = url
          vid.load()
        }
        vid.muted = true
        vid.loop = true
        const tryPlay = () => {
          if (!vid.src || vid.dataset.loadedUrl !== url) return
          vid.currentTime = 0
          vid.play().catch(() => {})
        }
        if (vid.readyState >= 3) { tryPlay() }
        else {
          vid._hoverPlayHandler = tryPlay
          vid.addEventListener('canplay', vid._hoverPlayHandler, { once: true })
        }
      })
    },
    onWfThumbLeave(wf) {
      const media = this.wfThumbHoverMedia(wf)
      if (media && media.isVideo) {
        const vid = this.getWfVideoEl(wf)
        if (vid) {
          if (vid._hoverPlayHandler) {
            vid.removeEventListener('canplay', vid._hoverPlayHandler)
            vid._hoverPlayHandler = null
          }
          vid.pause()
          vid.removeAttribute('src')
          delete vid.dataset.loadedUrl
          vid.load()
        }
      }
      this.hoveredWfThumbId = null
    },
    async loadSvipWorkflows() {
      this.wfLoading = true
      try {
        const res = await getWorkflowList({ pageNum: 1, pageSize: 20, isHot: 1 })
        if (!isBizSuccess(res)) return
        const rows = extractWorkflowRows(res)
        const all = rowsToNormalizedWorkflows(rows)
        const svip = all.filter((w) => w.isSvipFree)
        this.svipWorkflows = (svip.length >= 4 ? svip : all).slice(0, WF_MAX_ITEMS)
      } catch (err) {
        console.warn('[HomePage] loadSvipWorkflows failed', err)
      } finally {
        this.wfLoading = false
      }
    },
  },
}
</script>

<style scoped>
/* ── Shell ── */
.hp-root {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  padding-top: 72px;
}

/* ══════════════════════════════════════
   1. BANNER – peek 效果
   原理：track 宽度 = viewport 宽度，translateX % 基准一致
   每张 slide = 82% viewport 宽，gap = 2px
   初始偏移 = 9%（(100%-82%)/2）
══════════════════════════════════════ */
.hp-banner-section {
  width: 100%;
  background: #000;
  --hp-banner-slide: 82%;
  --hp-banner-gap: 2px;
  --hp-banner-offset: 9%;
}
.hp-banner-viewport {
  position: relative;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 16/6;
  max-height: 480px;
}
.hp-banner-track {
  display: flex;
  width: 100%;   /* 等于 viewport 宽，translateX % 才正确 */
  height: 100%;
  gap: var(--hp-banner-gap);
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}
.hp-banner-track.is-no-transition {
  transition: none;
}
.hp-banner-fade {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 16%;
  z-index: 5;
  pointer-events: none;
}
.hp-banner-fade--left {
  left: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.62) 0%, rgba(0, 0, 0, 0.28) 45%, transparent 100%);
}
.hp-banner-fade--right {
  right: 0;
  background: linear-gradient(to left, rgba(0, 0, 0, 0.62) 0%, rgba(0, 0, 0, 0.28) 45%, transparent 100%);
}
.hp-banner-slide {
  flex: 0 0 var(--hp-banner-slide);    /* 两侧各 peek 9% */
  border-radius: 14px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  opacity: 0.55;
  transform: scale(0.97);
  transition: opacity 0.4s, transform 0.4s;
}
.hp-banner-slide.is-active {
  opacity: 1;
  transform: scale(1);
}
.hp-banner-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.hp-banner-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(15,23,42,0.55);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}
.hp-banner-nav:hover { background: rgba(15,23,42,0.85); }
/* 箭头固定在中间 banner 左右边缘内侧，不贴 viewport 两侧 */
.hp-banner-nav--prev { left: calc(var(--hp-banner-offset) - 100px); }
.hp-banner-nav--next { right: calc(var(--hp-banner-offset) - 100px); }
.hp-banner-dots {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  z-index: 10;
}
.hp-banner-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: rgba(255,255,255,0.4);
  cursor: pointer;
  transition: background 0.2s, width 0.2s;
}
.hp-banner-dot.is-active { background: #fff; width: 18px; border-radius: 3px; }

/* ══════════════════════════════════════
   2. 功能入口 – 通栏单行
══════════════════════════════════════ */
.hp-entry-section {
  display: flex;
  gap: 12px;
  width: 100%;
  margin-top: 20px;
  padding: 0 20px;
  box-sizing: border-box;
}
.hp-entry-card {
  flex: 1 1 0;
  min-width: 0;
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px;
  text-decoration: none;
  color: var(--text);
  cursor: pointer;
  overflow: hidden;
  border: 1px solid var(--entry-border, var(--border, #1e3a5f));
  border-radius: 14px;
  background: var(--entry-bg, var(--bg-card, #0d1b2e));
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  min-height: 84px;
}
.hp-entry-card--featured {
  flex: 1.18 1 0;
  min-height: 92px;
  background: var(--entry-bg-featured, var(--entry-bg, var(--bg-card, #0d1b2e)));
  box-shadow: inset 0 3px 0 var(--entry-accent, var(--primary));
}
.hp-entry-card:focus-visible {
  outline: 2px solid var(--entry-accent, var(--primary));
  outline-offset: 2px;
  z-index: 1;
}

/* ── 浅色模式：深色实色卡片，高对比突出 ── */
html[data-theme="light"] .hp-entry-section {
  background: transparent;
}
html[data-theme="light"] .hp-entry-card {
  color: var(--entry-text, #ffffff);
  box-shadow: 0 4px 14px var(--entry-shadow, #cbd5e1);
}
html[data-theme="light"] .hp-entry-card:hover {
  background: var(--entry-bg-hover, var(--entry-bg));
  border-color: var(--entry-border-hover, var(--entry-border));
  box-shadow: 0 8px 24px var(--entry-shadow-hover, var(--entry-shadow, #94a3b8));
}
html[data-theme="light"] .hp-entry-card--featured:hover {
  background: var(--entry-bg-featured-hover, var(--entry-bg-featured, var(--entry-bg-hover)));
  box-shadow:
    inset 0 3px 0 var(--entry-top-accent, var(--entry-accent)),
    0 8px 24px var(--entry-shadow-hover, var(--entry-shadow, #94a3b8));
}
html[data-theme="light"] .hp-entry-glow {
  display: none;
}
html[data-theme="light"] .hp-entry-name {
  color: var(--entry-text, #ffffff);
}
html[data-theme="light"] .hp-entry-desc {
  color: var(--entry-text-muted, #e2e8f0);
}
html[data-theme="light"] .hp-entry-arrow {
  color: var(--entry-text-muted, #e2e8f0);
  opacity: 0.85;
}
html[data-theme="light"] .hp-entry-card:hover .hp-entry-arrow {
  color: #ffffff;
  opacity: 1;
}
html[data-theme="light"] .hp-entry-icon {
  background: var(--entry-icon-bg, #312e81);
  border-color: var(--entry-icon-border, #818cf8);
  color: var(--entry-icon-color, #e0e7ff);
  box-shadow: 0 2px 8px #1e1b4b;
}
html[data-theme="light"] .hp-entry-badge {
  color: var(--entry-badge-text, #ffffff);
  background: var(--entry-badge-bg, #312e81);
  border-color: var(--entry-badge-border, #818cf8);
}
html[data-theme="light"] .hp-entry-card--featured {
  box-shadow:
    inset 0 3px 0 var(--entry-top-accent, var(--entry-accent)),
    0 4px 14px var(--entry-shadow, #cbd5e1);
}
html[data-theme="light"] .hp-entry-card--quick-img {
  --entry-top-accent: #a5b4fc;
  --entry-accent: #c7d2fe;
  --entry-text: #ffffff;
  --entry-text-muted: #c7d2fe;
  --entry-bg: #4f46e5;
  --entry-bg-hover: #4338ca;
  --entry-bg-featured: #4338ca;
  --entry-bg-featured-hover: #3730a3;
  --entry-border: #6366f1;
  --entry-border-hover: #818cf8;
  --entry-shadow: #a5b4fc;
  --entry-shadow-hover: #818cf8;
  --entry-icon-bg: #3730a3;
  --entry-icon-border: #a5b4fc;
  --entry-icon-color: #e0e7ff;
  --entry-badge-bg: #312e81;
  --entry-badge-border: #a5b4fc;
  --entry-badge-text: #e0e7ff;
}
html[data-theme="light"] .hp-entry-card--quick-video {
  --entry-top-accent: #f9a8d4;
  --entry-accent: #fbcfe8;
  --entry-text: #ffffff;
  --entry-text-muted: #fce7f3;
  --entry-bg: #db2777;
  --entry-bg-hover: #be185d;
  --entry-bg-featured: #be185d;
  --entry-bg-featured-hover: #9d174d;
  --entry-border: #ec4899;
  --entry-border-hover: #f472b6;
  --entry-shadow: #f9a8d4;
  --entry-shadow-hover: #f472b6;
  --entry-icon-bg: #9d174d;
  --entry-icon-border: #f9a8d4;
  --entry-icon-color: #fce7f3;
  --entry-badge-bg: #831843;
  --entry-badge-border: #f9a8d4;
  --entry-badge-text: #fce7f3;
}
html[data-theme="light"] .hp-entry-card--ai-apps {
  --entry-top-accent: #93c5fd;
  --entry-accent: #bfdbfe;
  --entry-text: #ffffff;
  --entry-text-muted: #dbeafe;
  --entry-bg: #2563eb;
  --entry-bg-hover: #1d4ed8;
  --entry-bg-featured: #1d4ed8;
  --entry-bg-featured-hover: #1e40af;
  --entry-border: #3b82f6;
  --entry-border-hover: #60a5fa;
  --entry-shadow: #93c5fd;
  --entry-shadow-hover: #60a5fa;
  --entry-icon-bg: #1e40af;
  --entry-icon-border: #93c5fd;
  --entry-icon-color: #dbeafe;
  --entry-badge-bg: #1e3a8a;
  --entry-badge-border: #93c5fd;
  --entry-badge-text: #dbeafe;
}
html[data-theme="light"] .hp-entry-card--workflow {
  --entry-top-accent: #6ee7b7;
  --entry-accent: #a7f3d0;
  --entry-text: #ffffff;
  --entry-text-muted: #d1fae5;
  --entry-bg: #059669;
  --entry-bg-hover: #047857;
  --entry-bg-featured: #047857;
  --entry-bg-featured-hover: #065f46;
  --entry-border: #10b981;
  --entry-border-hover: #34d399;
  --entry-shadow: #6ee7b7;
  --entry-shadow-hover: #34d399;
  --entry-icon-bg: #065f46;
  --entry-icon-border: #6ee7b7;
  --entry-icon-color: #d1fae5;
  --entry-badge-bg: #064e3b;
  --entry-badge-border: #6ee7b7;
  --entry-badge-text: #d1fae5;
}
html[data-theme="light"] .hp-entry-card--inspiration {
  --entry-top-accent: #fcd34d;
  --entry-accent: #fde68a;
  --entry-text: #ffffff;
  --entry-text-muted: #fef3c7;
  --entry-bg: #d97706;
  --entry-bg-hover: #b45309;
  --entry-bg-featured: #b45309;
  --entry-bg-featured-hover: #92400e;
  --entry-border: #f59e0b;
  --entry-border-hover: #fbbf24;
  --entry-shadow: #fcd34d;
  --entry-shadow-hover: #fbbf24;
  --entry-icon-bg: #92400e;
  --entry-icon-border: #fcd34d;
  --entry-icon-color: #fef3c7;
  --entry-badge-bg: #78350f;
  --entry-badge-border: #fcd34d;
  --entry-badge-text: #fef3c7;
}

/* ── 深色模式：圆角卡片 + 色块区分 ── */
html[data-theme="dark"] .hp-entry-section {
  background: transparent;
}
html[data-theme="dark"] .hp-entry-card {
  background: var(--entry-bg, #0d1b2e);
  border-color: var(--entry-border, #1e3a5f);
}
html[data-theme="dark"] .hp-entry-card--featured {
  background: var(--entry-bg-featured, var(--entry-bg));
  box-shadow:
    inset 0 3px 0 var(--entry-accent),
    0 2px 8px #020617;
}
html[data-theme="dark"] .hp-entry-card:hover {
  background: var(--entry-bg-hover, var(--entry-bg));
  border-color: var(--entry-border-hover, var(--entry-border));
  box-shadow: 0 4px 16px #020617;
}
html[data-theme="dark"] .hp-entry-card--featured:hover {
  background: var(--entry-bg-featured-hover, var(--entry-bg-hover));
}
html[data-theme="dark"] .hp-entry-glow {
  opacity: 0.85;
  filter: blur(20px);
}
html[data-theme="dark"] .hp-entry-icon {
  background: var(--entry-icon-bg);
  border-color: var(--entry-icon-border, color-mix(in srgb, var(--entry-accent) 45%, #1e3a5f));
  box-shadow: 0 2px 10px color-mix(in srgb, var(--entry-accent) 28%, #020617);
}
html[data-theme="dark"] .hp-entry-badge {
  background: var(--entry-badge-bg);
  border-color: var(--entry-badge-border, color-mix(in srgb, var(--entry-accent) 38%, #1e3a5f));
}
html[data-theme="dark"] .hp-entry-card--quick-img {
  --entry-bg: #151a3d;
  --entry-bg-hover: #1c2252;
  --entry-bg-featured: #1c2252;
  --entry-bg-featured-hover: #252b6a;
  --entry-border: #312e81;
  --entry-border-hover: #4338ca;
  --entry-icon-bg: #1e1b4b;
  --entry-icon-border: #4338ca;
  --entry-badge-bg: #1e1b4b;
  --entry-badge-border: #4338ca;
}
html[data-theme="dark"] .hp-entry-card--quick-video {
  --entry-bg: #2a1228;
  --entry-bg-hover: #3b1536;
  --entry-bg-featured: #3b1536;
  --entry-bg-featured-hover: #4c1945;
  --entry-border: #831843;
  --entry-border-hover: #be185d;
  --entry-icon-bg: #500724;
  --entry-icon-border: #be185d;
  --entry-badge-bg: #500724;
  --entry-badge-border: #be185d;
}
html[data-theme="dark"] .hp-entry-card--ai-apps {
  --entry-bg: #0f1f3d;
  --entry-bg-hover: #122a52;
  --entry-bg-featured: #122a52;
  --entry-bg-featured-hover: #163566;
  --entry-border: #1e3a8a;
  --entry-border-hover: #2563eb;
  --entry-icon-bg: #172554;
  --entry-icon-border: #2563eb;
  --entry-badge-bg: #172554;
  --entry-badge-border: #2563eb;
}
html[data-theme="dark"] .hp-entry-card--workflow {
  --entry-bg: #0a1f1a;
  --entry-bg-hover: #0d2b22;
  --entry-bg-featured: #0d2b22;
  --entry-bg-featured-hover: #10372c;
  --entry-border: #065f46;
  --entry-border-hover: #059669;
  --entry-icon-bg: #064e3b;
  --entry-icon-border: #059669;
  --entry-badge-bg: #064e3b;
  --entry-badge-border: #059669;
}
html[data-theme="dark"] .hp-entry-card--inspiration {
  --entry-bg: #1f1608;
  --entry-bg-hover: #2a1e0a;
  --entry-bg-featured: #2a1e0a;
  --entry-bg-featured-hover: #35260c;
  --entry-border: #92400e;
  --entry-border-hover: #d97706;
  --entry-icon-bg: #451a03;
  --entry-icon-border: #d97706;
  --entry-badge-bg: #451a03;
  --entry-badge-border: #d97706;
}

/* 各卡片配色 */
.hp-entry-card--quick-img {
  --entry-accent: #6366f1;
  --eblob: radial-gradient(ellipse 85% 130% at 0% 50%, rgba(99, 102, 241, 0.42) 0%, transparent 72%);
  --eicon-bg: linear-gradient(135deg, rgba(99, 102, 241, 0.28) 0%, rgba(139, 92, 246, 0.14) 100%);
}
.hp-entry-card--quick-video {
  --entry-accent: #ec4899;
  --eblob: radial-gradient(ellipse 85% 130% at 0% 50%, rgba(236, 72, 153, 0.42) 0%, transparent 72%);
  --eicon-bg: linear-gradient(135deg, rgba(236, 72, 153, 0.28) 0%, rgba(244, 114, 182, 0.14) 100%);
}
.hp-entry-card--ai-apps {
  --entry-accent: #3b82f6;
  --eblob: radial-gradient(ellipse 75% 110% at 10% 50%, rgba(59, 130, 246, 0.34) 0%, transparent 70%);
  --eicon-bg: linear-gradient(135deg, rgba(59, 130, 246, 0.24) 0%, rgba(6, 182, 212, 0.12) 100%);
}
.hp-entry-card--workflow {
  --entry-accent: #10b981;
  --eblob: radial-gradient(ellipse 75% 110% at 10% 50%, rgba(16, 185, 129, 0.32) 0%, transparent 70%);
  --eicon-bg: linear-gradient(135deg, rgba(16, 185, 129, 0.24) 0%, rgba(52, 211, 153, 0.12) 100%);
}
.hp-entry-card--inspiration {
  --entry-accent: #f59e0b;
  --eblob: radial-gradient(ellipse 75% 110% at 10% 50%, rgba(245, 158, 11, 0.32) 0%, transparent 70%);
  --eicon-bg: linear-gradient(135deg, rgba(245, 158, 11, 0.24) 0%, rgba(251, 191, 36, 0.12) 100%);
}

.hp-entry-glow {
  position: absolute;
  inset: 0;
  background: var(--eblob);
  pointer-events: none;
  filter: blur(16px);
  z-index: 0;
  opacity: 0.95;
}
.hp-entry-icon {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--entry-icon-bg, var(--eicon-bg));
  color: var(--entry-accent);
  border: 1px solid var(--entry-icon-border, color-mix(in srgb, var(--entry-accent) 28%, transparent));
}
.hp-entry-icon--sm {
  width: 40px;
  height: 40px;
  border-radius: 12px;
}
.hp-entry-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}
.hp-entry-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.hp-entry-name {
  font-size: 15px;
  font-weight: 800;
  color: var(--text);
  white-space: nowrap;
}
.hp-entry-card:not(.hp-entry-card--featured) .hp-entry-name {
  font-size: 15px;
  font-weight: 700;
}
.hp-entry-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--entry-accent);
  background: var(--entry-badge-bg, color-mix(in srgb, var(--entry-accent) 14%, transparent));
  border: 1px solid var(--entry-badge-border, color-mix(in srgb, var(--entry-accent) 28%, transparent));
}
.hp-entry-desc {
  font-size: 12px;
  color: var(--text-muted, #64748b);
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hp-entry-arrow {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  color: var(--text-muted, #64748b);
  opacity: 0.45;
  transition: opacity 0.2s ease, transform 0.2s ease, color 0.2s ease;
}
.hp-entry-card:hover .hp-entry-arrow {
  opacity: 1;
  transform: translateX(3px);
  color: var(--entry-accent, var(--primary));
}

@media (prefers-reduced-motion: reduce) {
  .hp-entry-card,
  .hp-entry-arrow {
    transition: none;
  }
}

/* ══════════════════════════════════════
   3. AI应用 – 发现页风格（照搬灵感广场样式）
══════════════════════════════════════ */
.hp-aiapp-section {
  padding: 40px 0 20px;
  background: var(--bg);
}
.hp-aiapp-hd-main {
  display: flex;
  align-items: baseline;
  gap: 14px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
}
.hp-aiapp-subtitle {
  margin: 0;
  font-size: 14px;
  color: var(--text-muted, #64748b);
  line-height: 1.5;
}
.hp-section-hd--full {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 28px 16px;
}
.hp-section-title {
  font-size: 22px;
  font-weight: 800;
  color: var(--text);
  margin: 0;
}
.hp-section-more {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 13px;
  color: var(--primary, #3B82F6);
  text-decoration: none;
  transition: opacity 0.2s;
  flex-shrink: 0;
}
.hp-section-more:hover { opacity: 0.8; }

.hp-aiapp-discover {
  padding: 0;
  --hp-ai-gap: 10px;
  --hp-ai-visible: 7;
  --hp-ai-ratio: 214 / 350;
}

.ip-discover {
  padding: 8px 0 0;
}

.ip-section {
  display: grid;
  grid-template-columns: repeat(calc(var(--hp-ai-visible) + 1), minmax(0, 1fr));
  gap: var(--hp-ai-gap, 10px);
  padding: 20px 32px;
  align-items: stretch;
  border-bottom: 1px solid var(--border-light);
}
.ip-section:last-child { border-bottom: none; }

.ip-section-info {
  grid-column: 1;
  width: 100%;
  min-width: 0;
  max-width: none;
  aspect-ratio: var(--hp-ai-ratio, 214 / 350);
  height: auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 6px;
  padding: clamp(12px, 1.4vw, 18px);
  border-radius: 12px;
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  overflow: hidden;
}
.ip-section-info:hover {
  border-color: var(--border);
  box-shadow: var(--shadow);
}

.ip-section-head {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
}

.ip-section-tag {
  display: inline-block;
  padding: 2px 9px;
  border-radius: 100px;
  font-size: 11px;
  font-weight: 600;
  background: var(--cat-color, var(--primary));
  color: #fff;
  width: fit-content;
  letter-spacing: 0.02em;
}

.ip-section-title {
  font-size: clamp(14px, 1.1vw, 22px);
  font-weight: 700;
  color: var(--text);
  margin: 0;
  line-height: 2.2;
}

.ip-section-desc {
  font-size: clamp(12px, 0.78vw, 14px);
  color: var(--text-muted);
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ip-section-cover {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: 0;
  overflow: hidden;
}

.ip-section-fan {
  position: relative;
  width: 92%;
  max-width: 198px;
  aspect-ratio: 198 / 134;
  height: auto;
  margin: 0 auto;
}

.ip-section-fan-card {
  position: absolute;
  width: 46%;
  aspect-ratio: 68 / 92;
  height: auto;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid var(--bg-card);
  box-shadow: var(--shadow-sm);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.ip-section-fan-card--0 {
  left: 0;
  top: 10%;
  transform: rotate(-10deg);
  z-index: 1;
}
.ip-section-fan-card--1 {
  left: 27%;
  top: 0;
  transform: rotate(0deg);
  z-index: 3;
  box-shadow: var(--shadow);
}
.ip-section-fan-card--2 {
  left: 54%;
  top: 10%;
  transform: rotate(10deg);
  z-index: 2;
}
.ip-section-info:hover .ip-section-fan-card--0 {
  transform: rotate(-12deg) translateY(-3px);
}
.ip-section-info:hover .ip-section-fan-card--1 {
  transform: rotate(0deg) translateY(-5px);
  box-shadow: var(--shadow-lg);
}
.ip-section-info:hover .ip-section-fan-card--2 {
  transform: rotate(12deg) translateY(-3px);
}

.ip-section-more {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: clamp(12px, 0.78vw, 14px);
  color: var(--text-muted);
  flex-shrink: 0;
  transition: color 0.2s;
}
.ip-section-info:hover .ip-section-more { color: var(--primary); }

.ip-track-wrap {
  grid-column: 2 / -1;
  position: relative;
  width: 100%;
  min-width: 0;
  min-height: 0;
  align-self: stretch;
  display: flex;
  align-items: stretch;
  overflow: hidden;
}

.ip-hscroll {
  flex: 1;
  display: flex;
  gap: var(--hp-ai-gap, 10px);
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  align-items: stretch;
  height: 100%;
  min-height: 0;
}
.ip-hscroll::-webkit-scrollbar { display: none; }

.ip-hcard {
  flex: 0 0 calc((100% - (var(--hp-ai-visible) - 1) * var(--hp-ai-gap)) / var(--hp-ai-visible));
  width: calc((100% - (var(--hp-ai-visible) - 1) * var(--hp-ai-gap)) / var(--hp-ai-visible));
  height: 100%;
  min-height: 0;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  background: var(--bg-alt);
  border: 1px solid var(--border-light);
  box-sizing: border-box;
  transition: box-shadow 0.22s, border-color 0.22s;
}
.ip-hcard:hover {
  box-shadow: var(--shadow-lg);
  border-color: var(--border);
}

.ip-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(13, 27, 46, 0.72);
  border: 1px solid var(--border);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
  box-shadow: var(--shadow-sm);
}
.ip-nav-btn--prev { left: 6px; }
.ip-nav-btn--next { right: 6px; }
.ip-nav-btn:hover {
  background: var(--bg-alt);
  color: var(--primary);
  border-color: var(--primary);
}

.ip-hcard-media {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.ip-hcard-img,
.ip-hcard-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ip-hcard-video {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.3s;
}
.ip-card-video--playing {
  opacity: 1;
}

.ip-hcard-play-icon {
  position: absolute;
  top: 10px;
  right: 10px;
  transition: opacity 0.2s;
  pointer-events: none;
  z-index: 2;
}

.ip-card-dock {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  padding: 28px 10px 10px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.62) 0%, rgba(0, 0, 0, 0.28) 45%, transparent 100%);
  pointer-events: none;
}

.ip-card-title {
  font-size: clamp(11px, 0.85vw, 13px);
  color: #fff;
  line-height: 1.4;
  font-weight: 500;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transform: translateY(0);
  transition: transform 0.22s ease;
}

.ip-same-btn {
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  background: var(--gradient-brand);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  pointer-events: auto;
  opacity: 0;
  visibility: hidden;
  transform: translateY(6px);
  box-shadow: var(--shadow-sm);
  transition: opacity 0.22s ease, transform 0.22s ease, visibility 0.22s ease, box-shadow 0.22s ease;
}
.ip-same-btn:hover {
  box-shadow: var(--shadow);
}

.ip-hcard:hover .ip-card-title {
  transform: translateY(-42px);
}
.ip-hcard:hover .ip-same-btn {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

/* ══════════════════════════════════════
   4. 工作流 – 与 AI 应用卡片同尺寸，每行最多 8 个
══════════════════════════════════════ */
.hp-wf-section {
  --hp-wf-cols: 8;
  --hp-ai-gap: 10px;
  --hp-ai-ratio: 214 / 350;
  padding: 20px 0 48px;
  background: var(--bg);
}
.hp-wf-grid {
  display: grid;
  grid-template-columns: repeat(var(--hp-wf-cols), minmax(0, 1fr));
  gap: var(--hp-ai-gap, 10px);
  padding: 0 32px;
}
.hp-wf-grid .ip-hcard {
  flex: none;
  width: 100%;
  height: auto;
  aspect-ratio: var(--hp-ai-ratio, 214 / 350);
}
.hp-wf-media-stack {
  position: absolute;
  inset: 0;
  transition: transform 0.35s ease;
}
.hp-wf-card:hover .hp-wf-media-stack {
  transform: scale(1.05);
}
.hp-wf-cover-img {
  transition: opacity 0.22s ease;
}
.hp-wf-card.is-wf-thumb-hovering .hp-wf-cover-img {
  opacity: 0;
}
.hp-wf-hover-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 2;
  opacity: 0;
  transition: opacity 0.22s ease;
  pointer-events: none;
}
.hp-wf-card.is-wf-thumb-hovering .hp-wf-hover-layer {
  opacity: 1;
}
.hp-wf-svip-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 4;
  padding: 2px 8px;
  border-radius: 4px;
  background: linear-gradient(135deg, #7C3AED, #A855F7);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  pointer-events: none;
}

/* ── 状态 ── */
.hp-state-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 140px;
  color: var(--text-muted, #64748b);
  font-size: 13px;
}
.hp-state-loading--sm { min-height: 100px; padding: 0 28px; justify-content: flex-start; }
.hp-state-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  color: var(--text-muted, #64748b);
  font-size: 13px;
}
.hp-spinner {
  width: 20px; height: 20px;
  border: 2px solid var(--border-light, #122240);
  border-top-color: var(--primary, #3B82F6);
  border-radius: 50%;
  animation: hp-spin 0.7s linear infinite;
}
@keyframes hp-spin { to { transform: rotate(360deg); } }

/* ── 响应式 ── */
@media (max-width: 1100px) {
  .hp-entry-card { padding: 16px 14px; gap: 10px; }
  .hp-entry-icon { width: 42px; height: 42px; }
  .hp-entry-icon--sm { width: 36px; height: 36px; }
  .hp-aiapp-discover { --hp-ai-visible: 5; }
  .hp-wf-section { --hp-wf-cols: 5; }
}

@media (max-width: 768px) {
  .hp-entry-section {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding: 0 16px;
    gap: 10px;
  }
  .hp-entry-section::-webkit-scrollbar { display: none; }
  .hp-entry-card {
    flex: 0 0 168px;
    min-width: 168px;
    padding: 14px 16px;
    min-height: 76px;
  }
  .hp-entry-card--featured {
    flex: 0 0 188px;
    min-width: 188px;
  }
  .hp-entry-icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
  }
  .hp-entry-desc { display: none; }
  .hp-section-hd--full { padding: 0 16px 10px; }
  .hp-aiapp-hd-main { flex-direction: column; align-items: flex-start; gap: 6px; }
  .hp-aiapp-subtitle { font-size: 13px; }
  .hp-aiapp-discover { --hp-ai-visible: 3; }
  .ip-section { padding: 16px; }
  .ip-nav-btn { display: none; }
  .hp-wf-section { --hp-wf-cols: 3; }
  .hp-wf-grid { padding: 0 16px; }
  .hp-banner-viewport { aspect-ratio: 16/7; }
  .hp-banner-section {
    --hp-banner-slide: 88%;
    --hp-banner-offset: 6%;
  }
  .hp-banner-slide { flex: 0 0 var(--hp-banner-slide); }
  .hp-banner-fade { width: 12%; }
  .hp-banner-nav--prev { left: calc(var(--hp-banner-offset) - 90px); }
  .hp-banner-nav--next { right: calc(var(--hp-banner-offset) - 90px); }
}
</style>
